import { useMemo, useState } from "react";
import { Button, Input, PageBody, useToast } from "@astrum/ui";
import { HELP_ARTICLES, parseMarkdown, searchArticles, type Inline } from "../lib/help.js";
import { useAuth } from "../lib/auth.js";
import { setTourCompleted } from "../components/FirstRunTour.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { useShortcuts } from "../shell/shortcuts-context.js";
import "./help.css";

function Inlines({ items }: { items: Inline[] }) {
  return (
    <>
      {items.map((item, i) =>
        item.kind === "bold" ? (
          <strong key={i}>{item.text}</strong>
        ) : item.kind === "code" ? (
          <code key={i}>{item.text}</code>
        ) : item.kind === "link" ? (
          <a key={i} href={item.href} target="_blank" rel="noreferrer noopener">
            {item.text}
          </a>
        ) : (
          <span key={i}>{item.text}</span>
        ),
      )}
    </>
  );
}

/** Link to a fuller help site; absent unless the deploy sets VITE_HELP_URL. */
const HELP_URL: string | undefined = import.meta.env.VITE_HELP_URL || undefined;

export function Help() {
  usePageTitle("Help");
  const { refresh } = useAuth();
  const { toast } = useToast();
  const shortcuts = useShortcuts();
  const [query, setQuery] = useState("");
  const [slug, setSlug] = useState(HELP_ARTICLES[0]?.slug);
  const results = useMemo(() => searchArticles(HELP_ARTICLES, query), [query]);
  const article = results.find((a) => a.slug === slug) ?? results[0];

  const restartTour = async () => {
    try {
      await setTourCompleted(false, refresh);
    } catch {
      toast("Could not restart the tour. Try again.", { tone: "danger" });
    }
  };

  return (
    <PageBody className="help" title="Help" width="wide">
      <div className="help__tools">
        <Input
          type="search"
          aria-label="Search help"
          placeholder="Search help"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button variant="ghost" onClick={() => void restartTour()}>
          Show the tour again
        </Button>
        <Button variant="ghost" onClick={shortcuts.show}>
          Keyboard shortcuts
        </Button>
        {HELP_URL && (
          <a className="help__more" href={HELP_URL} target="_blank" rel="noreferrer noopener">
            More help online
          </a>
        )}
      </div>
      <div className="help__layout">
        <nav aria-label="Help articles">
          {results.length === 0 && <p className="help__empty">Nothing matches "{query}".</p>}
          <ul>
            {results.map((a) => (
              <li key={a.slug}>
                <button
                  type="button"
                  className={a.slug === article?.slug ? "is-active" : undefined}
                  aria-current={a.slug === article?.slug ? "page" : undefined}
                  onClick={() => setSlug(a.slug)}
                >
                  {a.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        {article && (
          <article className="help__article">
            <h2>{article.title}</h2>
            {parseMarkdown(article.body).map((block, i) =>
              block.kind === "h2" ? (
                <h3 key={i}>{block.text}</h3>
              ) : block.kind === "p" ? (
                <p key={i}>
                  <Inlines items={block.inlines} />
                </p>
              ) : block.kind === "ul" ? (
                <ul key={i}>
                  {block.items.map((item, j) => (
                    <li key={j}>
                      <Inlines items={item} />
                    </li>
                  ))}
                </ul>
              ) : (
                <ol key={i}>
                  {block.items.map((item, j) => (
                    <li key={j}>
                      <Inlines items={item} />
                    </li>
                  ))}
                </ol>
              ),
            )}
          </article>
        )}
      </div>
    </PageBody>
  );
}
